import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  IconButton,
  Typography,
  useEventCallback,
} from '@mui/material';
import {
  ChangeRelationshipVM,
  BodyChangeRelationship,
  RegistrationCitizenshipFormVM,
} from 'mutation/view-models/registration-citizenship';
import ParenthoodRelationshipList from 'shell/components/shared/parenthood-relationship-list';
import { Fragment, memo, useEffect, useMemo, useState } from 'react';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import Table from 'shell/components/shared/table';
import { noop } from 'shell/utils/noop';
import { headCells } from './constants';
import SaveIcon from '@mui/icons-material/Save';
import ReplayIcon from '@mui/icons-material/Replay';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseAnprData } from 'shell/models/person';
import { __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { transformFamilySearch, TransformFamilySearchProps } from './transform-family-search';
import { useFormikContext } from 'formik';
import { transformFamilyMembers } from './transform-family-members';

interface ChangeRelationshipProps {
  values?: ChangeRelationshipVM;
  onChangeValues: (value: ChangeRelationshipVM) => void;
  isNationalSearch?: string;
}

const ChangeRelationshipComponent = ({
  values,
  onChangeValues,
  isNationalSearch,
}: ChangeRelationshipProps): React.ReactElement => {
  const formContext = useFormikContext<RegistrationCitizenshipFormVM>();

  if (
    formContext.values.person?.subjectAire?.toUpperCase() !== 'S' || // non soggetto aire
    formContext.values.person?.codeLink !== '1' // non capo famiglia
  ) {
    return <></>;
  }

  const fiscalCode = useMemo(() => {
    return formContext.values.person?.fiscalCode || '';
  }, [formContext.values.person?.fiscalCode]);

  const idFamily = useMemo(() => {
    return formContext.values.person?.idFamigliaConvivenza || '';
  }, [formContext.values.person?.idFamigliaConvivenza]);

  const codeIstat = useMemo(() => {
    return formContext.values.person?.codeIstat || '';
  }, [formContext.values.person?.codeIstat]);

  const [open, setOpen] = useState(false);

  const [familyMembers, getFamilyMembers] = useHttpFetch<
    HttpResponse<ResponseAnprData>,
    Array<BodyChangeRelationship>
  >(`${__PERSON_NEW_API_URL__}/persona/ricerca-famiglia`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: [],
    },
    transformer: transformFamilyMembers,
  });

  useEffect(() => {
    const filter: TransformFamilySearchProps = {
      isNationalResearch: Boolean(isNationalSearch),
      fiscalCode,
      idFamily,
      codeIstat,
    };

    const data = transformFamilySearch(filter);

    getFamilyMembers({ data });
  }, [isNationalSearch, fiscalCode, idFamily]);

  useEffect(() => {
    if (familyMembers.data) {
      const newBody: BodyChangeRelationship[] = familyMembers.data as BodyChangeRelationship[];

      onChangeValues({
        ...values,
        count: newBody.length || 0,
        body: newBody || [],
      });
    }
  }, [familyMembers.data]);

  const onChangeRelationship = useEventCallback((id: string | undefined, index: number) => {
    const newBody = values?.body?.slice() || [];
    newBody[index].relationship = id || '';

    onChangeValues({
      ...values,
      body: newBody || [],
    });
  });

  const onReload = useEventCallback((index: number) => {
    const newBody = values?.body?.slice() || [];
    newBody[index].relationship = newBody[index]?.oldRelationship || '';

    onChangeValues({
      ...values,
      body: newBody || [],
    });
  });

  const body: BodyChangeRelationship[] = useMemo(() => {
    if (!values?.body) {
      return [];
    }

    return values?.body?.map((item, index) => {
      return {
        ...item,
        action: (
          <>
            <IconButton
              onClick={() => {
                // onVisibilityPress(); TODO
              }}
              aria-label="Visualizza elemento"
              aria-haspopup="true"
              style={{
                padding: '4px',
              }}
            >
              <SaveIcon />
            </IconButton>
            <IconButton
              onClick={() => {
                onReload(index);
              }}
              aria-label="Visualizza elemento"
              aria-haspopup="true"
              style={{
                padding: '4px',
              }}
            >
              <ReplayIcon />
            </IconButton>
          </>
        ),
        relationship: (
          <ParenthoodRelationshipList
            value={item.relationship?.toString() || ''}
            onChange={(value) => onChangeRelationship(value, index)}
            label=""
          />
        ),
      };
    });
  }, [values?.body]);

  const onChangePage = useEventCallback((page: number) => {
    onChangeValues({
      ...values,
      page: page || 0,
    });
  });

  const onChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    onChangeValues({
      ...values,
      rowsPerPage: rowsPerPage || 0,
      page: 0,
    });
  });

  return (
    <Fragment>
      <Accordion
        expanded={open}
        onChange={() => {
          setOpen(!open);
        }}
      >
        <AccordionSummary
          aria-controls="panel-person-content"
          id="panel-person-header"
          expandIcon={<ExpandMore />}
        >
          <Typography>Elenco soggetti per cambio parentela</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Table
            ariaLabel="Lista soggetti cambio parentela"
            page={values?.page || 0}
            rowsPerPage={values?.rowsPerPage}
            body={body}
            count={values?.count || 0}
            head={headCells}
            onSort={noop}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
            hideButtons
            orders={{}}
            isNotLayoutFixed
            noSort
          />
        </AccordionDetails>
      </Accordion>
    </Fragment>
  );
};

ChangeRelationshipComponent.displayName =
  'Accordion Registration Citizenship - Change Relationship component';

const ChangeRelationship = memo(ChangeRelationshipComponent);

export default ChangeRelationship;
